import { useState, useEffect, useRef } from "react";
import {
  collection,
  query,
  onSnapshot,
  DocumentData,
  Query,
  QuerySnapshot,
} from "firebase/firestore";
import { db } from "../../config/firebase";

interface FirestoreDocument extends DocumentData {
  id: string;
}

interface UseFirestoreCollectionListenerResult<T> {
  data: T[];
  error: string | null;
  isLoading: boolean;
}

/**
 * A reusable hook for listening to Firestore collection queries.
 * Sets up an onSnapshot listener and handles cleanup automatically.
 *
 * @param path - The Firestore collection path (e.g., "collection")
 * @param queryBuilder - Optional function to build a query (e.g., add where/orderBy clauses)
 * @returns Object containing the collection data, loading state, and any error that occurred
 */
export function useFirestoreCollectionListener<T extends FirestoreDocument>(
  path: string | null,
  queryBuilder?: (baseQuery: Query) => Query
): UseFirestoreCollectionListenerResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Clear state when path changes
    setData([]);
    setError(null);
    setIsLoading(true);

    // Clean up previous listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (!path) {
      setIsLoading(false);
      return;
    }

    try {
      // Create base query
      let baseQuery = query(collection(db, path));

      // Apply query modifiers if provided
      const finalQuery = queryBuilder ? queryBuilder(baseQuery) : baseQuery;

      // Set up new listener
      unsubscribeRef.current = onSnapshot(
        finalQuery,
        (snapshot: QuerySnapshot) => {
          const items = snapshot.docs.map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              }) as T
          );
          setData(items);
          setError(null);
          setIsLoading(false);
        },
        (err) => {
          setData([]);
          setError(err.message);
          setIsLoading(false);
        }
      );

      // Cleanup function
      return () => {
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
      };
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to set up collection listener"
      );
      setIsLoading(false);
      return;
    }
  }, [path, queryBuilder]);

  return { data, error, isLoading };
}
